import React from 'react';
import { Check } from 'lucide-react';

import { PhonePage, PrimaryButton, Spinner, TopBar } from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, MultiField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * Adding or editing a vendor, on a phone.
 *
 * The side pane's form, one control per row, with the stages they take work
 * at as chips that toggle. A stage somebody already covers says so in its
 * label, which is what the desktop's badge was for.
 */

const FIELDS = [
  ['full_name', 'Name *', 'Vendor or firm name', 'vendor-name-input', 'words'],
  ['contact_number', 'Contact', 'Phone number', 'vendor-contact-input', 'tel'],
  ['email', 'Email', 'Optional', 'vendor-email-input', 'email'],
  ['address', 'Address', 'Where the work goes', 'vendor-address-input'],
];

export default function PhoneVendorForm({ mode, form, onChange, availableStages, submitting, onSave, onCancel }) {
  const adding = mode === 'add';
  const set = (patch) => onChange((f) => ({ ...f, ...patch }));
  const stageOptions = availableStages.map((s) => ({
    value: s.stage_name,
    label: s.vendor_count > 0 ? `${s.stage_name} · ${s.vendor_count}` : s.stage_name,
  }));

  return (
    <PhonePage testID="vendor-form-page">
      <TopBar
        eyebrow="VENDOR"
        title={adding ? 'Add vendor' : `Edit ${mode.full_name || 'vendor'}`}
        subtitle={adding ? 'Login credentials are generated on save' : mode.username}
        onBack={onCancel}
        actions={(
          <button
            type="button"
            data-testid="submit-vendor-btn"
            onClick={onSave}
            disabled={submitting}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            {submitting ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : <><Check className="h-4 w-4" /> Save</>}
          </button>
        )}
      />

      <div className="flex-1 space-y-3 px-4 pb-10 pt-4">
        {FIELDS.map(([key, label, placeholder, testID, inputMode]) => (
          <Field key={key} label={label}>
            <input
              data-testid={testID}
              className={INPUT_CLASS}
              style={inputStyle}
              value={form[key]}
              placeholder={placeholder}
              inputMode={inputMode === 'words' ? undefined : inputMode}
              autoCapitalize={inputMode === 'words' ? 'words' : 'none'}
              onChange={(e) => set({ [key]: e.target.value })}
            />
          </Field>
        ))}

        <Field label={`Takes work at${form.linked_stages.length ? ` (${form.linked_stages.length} selected)` : ''}`}>
          {availableStages.length === 0 ? (
            <p className="text-[13px]" style={{ color: Colors.text.tertiary }}>Loading stages…</p>
          ) : (
            <MultiField
              name="linked_stages"
              value={form.linked_stages}
              options={stageOptions}
              onChange={(names) => set({ linked_stages: names })}
            />
          )}
        </Field>

        <Field label="Remarks">
          <input
            data-testid="vendor-remarks-input"
            className={INPUT_CLASS} style={inputStyle} placeholder="Notes…"
            value={form.remarks}
            onChange={(e) => set({ remarks: e.target.value })}
          />
        </Field>

        <PrimaryButton className="mt-5" onClick={onSave} busy={submitting} testID="submit-vendor-bottom">
          <Check className="h-4 w-4" /> {adding ? 'Create vendor' : 'Save changes'}
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
